'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type MihrRole =
  | 'super_admin' |'operations_manager' |'hr_manager' |'accounts' |'employer' |'agent' |'candidate';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: MihrRole;
  avatar_url?: string;
  phone?: string;
  is_active: boolean;
  employer_id?: string;
  agent_id?: string;
  candidate_id?: string;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

export interface RolePermissions {
  can_view_all_candidates: boolean;
  can_edit_all_candidates: boolean;
  can_import_candidates: boolean;
  can_view_own_candidates: boolean;
  can_edit_own_candidates: boolean;
  can_view_all_vacancies: boolean;
  can_edit_all_vacancies: boolean;
  can_view_own_vacancies: boolean;
  can_edit_own_vacancies: boolean;
  can_view_all_financials: boolean;
  can_edit_financials: boolean;
  can_view_own_financials: boolean;
  can_manage_users: boolean;
  can_manage_roles: boolean;
  can_manage_settings: boolean;
  can_import_export_data: boolean;
  can_view_all_reports: boolean;
  can_view_audit_logs: boolean;
  can_view_employer_candidates: boolean;
  can_give_interview_feedback: boolean;
  can_register_candidates: boolean;
  can_view_agent_candidates: boolean;
  can_screen_candidates: boolean;
  can_schedule_interviews: boolean;
  can_generate_cvs: boolean;
  can_upload_documents: boolean;
  can_manage_pipeline: boolean;
  can_manage_deployment: boolean;
  can_manage_interviews: boolean;
  can_manage_agents: boolean;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  permissions: RolePermissions | null;
  loading: boolean;
  profileLoading: boolean;
  // Auth methods
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  // RBAC helpers
  hasRole: (role: MihrRole | MihrRole[]) => boolean;
  hasPermission: (permission: keyof RolePermissions) => boolean;
  isInternalStaff: () => boolean;
  isSuperAdmin: () => boolean;
  canAccessRoute: (route: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Role display names
export const ROLE_LABELS: Record<MihrRole, string> = {
  super_admin: 'Super Admin / CEO',
  operations_manager: 'Operations Manager',
  hr_manager: 'HR Manager',
  accounts: 'Accounts',
  employer: 'Employer',
  agent: 'Agent',
  candidate: 'Candidate',
};

// Routes accessible per role
const ROLE_ROUTE_ACCESS: Record<MihrRole, string[]> = {
  super_admin: ['/', '/candidate-management', '/vacancy-management', '/employer-management', '/agents', '/interviews', '/documents', '/deployment', '/payments', '/reports', '/hr', '/settings', '/user-management', '/candidate-portal'],
  operations_manager: ['/', '/candidate-management', '/vacancy-management', '/employer-management', '/agents', '/interviews', '/documents', '/deployment', '/reports', '/hr', '/candidate-portal'],
  hr_manager: ['/', '/candidate-management', '/vacancy-management', '/employer-management', '/interviews', '/documents', '/reports', '/candidate-portal'],
  accounts: ['/', '/payments', '/reports'],
  employer: ['/', '/vacancy-management', '/candidate-management', '/interviews'],
  agent: ['/', '/candidate-management', '/candidate-portal'],
  candidate: ['/', '/candidate-portal'],
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<RolePermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const supabase = createClient();
  const router = useRouter();

  const fetchProfileAndPermissions = useCallback(
    async (userId: string) => {
      setProfileLoading(true);
      try {
        // Fetch user profile
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (profileError) {
          console.error('Profile fetch error:', profileError.message);
          setProfile(null);
          setPermissions(null);
          return;
        }

        if (!profileData) {
          console.warn('No profile found for user:', userId);
          setProfile(null);
          setPermissions(null);
          return;
        }

        setProfile(profileData as UserProfile);

        // Fetch role permissions
        const { data: permData, error: permError } = await supabase
          .from('role_permissions')
          .select('*')
          .eq('role', profileData.role)
          .maybeSingle();

        if (permError) {
          console.error('Permissions fetch error:', permError.message);
        } else {
          setPermissions(permData as RolePermissions);
        }

        // Update last_login_at
        await supabase
          .from('user_profiles')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', userId);
      } catch (err) {
        console.error('fetchProfileAndPermissions error:', err);
      } finally {
        setProfileLoading(false);
      }
    },
    [supabase]
  );

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfileAndPermissions(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfileAndPermissions(session.user.id);
      } else {
        setProfile(null);
        setPermissions(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          role: metadata?.role || 'candidate',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    router.refresh();
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setPermissions(null);
    router.push('/login');
    router.refresh();
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data as UserProfile | null;
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfileAndPermissions(user.id);
    }
  };

  // RBAC helpers
  const hasRole = (role: MihrRole | MihrRole[]): boolean => {
    if (!profile) return false;
    if (Array.isArray(role)) return role.includes(profile.role);
    return profile.role === role;
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    if (!permissions) return false;
    return permissions[permission] === true;
  };

  const isInternalStaff = (): boolean => {
    if (!profile) return false;
    return ['super_admin', 'operations_manager', 'hr_manager', 'accounts'].includes(profile.role);
  };

  const isSuperAdmin = (): boolean => {
    return profile?.role === 'super_admin';
  };

  const canAccessRoute = (route: string): boolean => {
    if (!profile) return false;
    const allowedRoutes = ROLE_ROUTE_ACCESS[profile.role] || [];
    return allowedRoutes.some(
      (r) => route === r || (r !== '/' && route.startsWith(r))
    );
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    permissions,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    hasRole,
    hasPermission,
    isInternalStaff,
    isSuperAdmin,
    canAccessRoute,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
